<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import type { ProjectWorkflowView } from "@/types";

interface Props {
  show: boolean;
  currentVersion: string;
  productId: string;
  workflows?: ProjectWorkflowView[];
  isLoading?: boolean;
}

interface Emits {
  (e: "close"): void;
  (e: "create", productData: ProductCreationData): void;
}

interface WorkflowVersion {
  id: string;
  name: string;
  currentVersion: string;
  hasChanges: boolean;
  previousVersion?: string;
}

interface ProductCreationData {
  version: string;
  versionType: "patch" | "minor" | "major" | "custom";
  description: string;
  releaseNotes: string;
  workflowVersions: WorkflowVersion[];
  // The workflow ids the publisher chose to include in this version.
  selectedWorkflowIds: string[];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const formData = ref({
  versionType: "minor" as "patch" | "minor" | "major" | "custom",
  description: "",
  releaseNotes: "",
});

const customVersion = ref("");

// Loading state is owned by the parent (the create-version mutation); the modal just
// reflects it on the submit button so the user sees feedback during the API round-trip.
const isLoading = computed(() => !!props.isLoading);

// Only show workflows that have been published at least once
// w.version comes from the API as LatestPublishedVersion ?? "Draft"
const workflowVersions = computed<WorkflowVersion[]>(() => {
  if (!props.workflows?.length) return [];
  return props.workflows
    .filter((w) => w.version && w.version !== "Draft")
    .map((w) => ({
      id: w.id,
      name: w.name,
      currentVersion: w.version!,
      hasChanges: false,
    }));
});

// Ids the publisher has chosen to include. Pre-checked to every published workflow (the first-version
// default — "include everything"). A re-open resets the selection in resetForm().
const selectedIds = ref<string[]>([]);

function syncSelectionToPublished() {
  selectedIds.value = workflowVersions.value.map((w) => w.id);
}

// The workflow list arrives asynchronously (parent fetch) and may land after the modal opens, so seed
// the pre-check whenever it (re)populates while there's nothing selected yet.
watch(
  workflowVersions,
  (list) => {
    if (list.length && selectedIds.value.length === 0) syncSelectionToPublished();
  },
  { immediate: true },
);

const suggestedVersions = computed(() => {
  const current = props.currentVersion.replace("v", "");
  const parts = current.split(".").map((p) => {
    const n = Number(p);
    return isNaN(n) ? 0 : n;
  });

  return {
    patch: `${parts[0]}.${parts[1] || 0}.${(parts[2] || 0) + 1}`,
    minor: `${parts[0]}.${(parts[1] || 0) + 1}.0`,
    major: `${(parts[0] || 0) + 1}.0.0`,
  };
});

const currentVersionNumber = computed(() => {
  if (formData.value.versionType === "custom") {
    return customVersion.value || "Enter custom version";
  } else {
    return suggestedVersions.value[formData.value.versionType];
  }
});

watch(
  () => props.show,
  (newShow) => {
    if (newShow) {
      resetForm();
    }
  },
);

function resetForm() {
  formData.value = {
    versionType: "minor",
    description: "",
    releaseNotes: "",
  };
  customVersion.value = "";
  syncSelectionToPublished();
}

function closeModal() {
  // Refuse to close while the parent is mid-save — the request is in flight and the modal
  // shouldn't be dismissible until it resolves (success closes via the parent watching the
  // mutation, error keeps the modal open so the user can retry).
  if (isLoading.value) return;
  emit("close");
}

const canSubmit = computed(
  () => !!formData.value.description.trim() && selectedIds.value.length > 0,
);

function createProductVersion() {
  if (!canSubmit.value) {
    return;
  }

  const productData: ProductCreationData = {
    version: currentVersionNumber.value,
    versionType: formData.value.versionType,
    description: formData.value.description,
    releaseNotes: formData.value.releaseNotes,
    workflowVersions: workflowVersions.value,
    selectedWorkflowIds: [...selectedIds.value],
  };

  emit("create", productData);
}
</script>

<template>
  <div
    class="modal fade"
    :class="{ show: show, 'd-block': show }"
    tabindex="-1"
    :style="{ display: show ? 'block' : 'none' }"
    @click.self="closeModal"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">
            <i class="bi bi-plus-circle text-primary me-2"></i>
            Create Version {{ currentVersionNumber }}
          </h2>
          <button
            type="button"
            class="btn-close"
            @click="closeModal"
            :disabled="isLoading"
          ></button>
        </div>

        <div class="modal-body">
          <!-- Workflow selection — which published workflows this version contains -->
          <div class="mb-4">
            <h3 class="text-muted mb-1">
              Workflows to include
            </h3>
            <p class="text-muted small mb-3">
              Only the workflows you check are shipped in this version. Uncheck one to drop it from the
              product — it can then be deleted from the master.
            </p>

            <div v-if="workflowVersions.length" class="list-group list-group-flush">
              <label
                v-for="workflow in workflowVersions"
                :key="workflow.id"
                class="list-group-item border-0 px-0 py-2 bg-transparent d-flex align-items-center"
                :for="`wf-${workflow.id}`"
                role="button"
              >
                <input
                  class="form-check-input me-2 mt-0 flex-shrink-0"
                  type="checkbox"
                  :id="`wf-${workflow.id}`"
                  :value="workflow.id"
                  v-model="selectedIds"
                />
                <i class="bi bi-diagram-3 text-muted me-2"></i>
                <strong>{{ workflow.name }}</strong>
                <span class="ms-2 text-muted">{{ workflow.currentVersion }}</span>
                <span class="badge bg-success ms-2">published</span>
              </label>
            </div>
            <div v-else class="text-muted small">
              <i class="bi bi-info-circle me-1"></i>
              No workflows found in the master project.
            </div>

            <div v-if="workflowVersions.length && !selectedIds.length" class="text-danger small mt-2">
              <i class="bi bi-exclamation-triangle me-1"></i>
              Select at least one workflow to include in this version.
            </div>
          </div>

          <!-- Current Version Display -->
          <div v-if="currentVersion !== '—'" class="alert alert-info mb-4">
            <i class="bi bi-info-circle me-2"></i>
            <strong>Current Version: {{ currentVersion }}</strong>
          </div>

          <!-- Version Type Selection -->
          <div class="mb-4">
            <h3 class="mb-3">What type of update is this?</h3>

            <div class="form-check mb-3">
              <input
                class="form-check-input"
                type="radio"
                name="versionType"
                value="patch"
                id="patchVersion"
                v-model="formData.versionType"
              />
              <label class="form-check-label" for="patchVersion">
                <div class="d-flex align-items-center">
                  <strong
                    >Patch ({{ suggestedVersions.patch }}) - Bug fixes</strong
                  >
                  <span
                    v-if="formData.versionType === 'patch'"
                    class="badge bg-primary ms-2"
                    >Selected</span
                  >
                </div>
                <div class="text-muted small">
                  Minor bug fixes and security updates
                </div>
              </label>
            </div>

            <div class="form-check mb-3">
              <input
                class="form-check-input"
                type="radio"
                name="versionType"
                value="minor"
                id="minorVersion"
                v-model="formData.versionType"
              />
              <label class="form-check-label" for="minorVersion">
                <div class="d-flex align-items-center">
                  <strong
                    >Minor ({{ suggestedVersions.minor }}) - New
                    features</strong
                  >
                  <span
                    v-if="formData.versionType === 'minor'"
                    class="badge bg-primary ms-2"
                    >Selected</span
                  >
                </div>
                <div class="text-muted small">
                  New features, improvements, non-breaking changes
                </div>
              </label>
            </div>

            <div class="form-check mb-3">
              <input
                class="form-check-input"
                type="radio"
                name="versionType"
                value="major"
                id="majorVersion"
                v-model="formData.versionType"
              />
              <label class="form-check-label" for="majorVersion">
                <div class="d-flex align-items-center">
                  <strong
                    >Major ({{ suggestedVersions.major }}) - Breaking
                    changes</strong
                  >
                  <span
                    v-if="formData.versionType === 'major'"
                    class="badge bg-primary ms-2"
                    >Selected</span
                  >
                </div>
                <div class="text-muted small">
                  Major overhauls, breaking changes, new architecture
                </div>
              </label>
            </div>

            <div class="form-check mb-3">
              <input
                class="form-check-input"
                type="radio"
                name="versionType"
                value="custom"
                id="customVersion"
                v-model="formData.versionType"
              />
              <label class="form-check-label" for="customVersion">
                <div class="d-flex align-items-center">
                  <strong>Custom version number</strong>
                  <span
                    v-if="formData.versionType === 'custom'"
                    class="badge bg-secondary ms-2"
                    >Selected</span
                  >
                </div>
              </label>
            </div>

            <!-- Custom Version Input -->
            <div v-if="formData.versionType === 'custom'" class="ms-4 mb-3">
              <label class="form-label">Custom version:</label>
              <div class="input-group" style="width: 300px">
                <input
                  type="text"
                  class="form-control"
                  v-model="customVersion"
                  placeholder="e.g., 2.4.1-beta"
                />
              </div>
            </div>
          </div>

          <!-- Version Description -->
          <div class="mb-3">
            <label class="form-label"
              >Version Description <span class="text-danger">*</span></label
            >
            <input
              type="text"
              class="form-control"
              v-model="formData.description"
              placeholder="Brief description of what's new in this version"
              maxlength="200"
            />
            <div class="form-text">
              {{ formData.description.length }}/200 characters
            </div>
          </div>

          <!-- Release Notes -->
          <div class="mb-3">
            <label class="form-label">Release Notes</label>
            <textarea
              class="form-control"
              rows="4"
              v-model="formData.releaseNotes"
              placeholder="Detailed release notes for users (optional)"
            ></textarea>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="bb-btn-cancel" :disabled="isLoading" @click="closeModal">
            Cancel
          </button>
          <button
            type="button"
            class="bb-btn-primary-cta"
            :disabled="isLoading || !canSubmit"
            @click="createProductVersion"
          >
            <span
              v-if="isLoading"
              class="spinner-border spinner-border-sm me-1"
            ></span>
            <i v-else class="bi bi-plus-circle me-1"></i>
            {{ isLoading ? "Creating…" : `Create Version ${currentVersionNumber}` }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-if="show" class="modal-backdrop fade show" @click="closeModal"></div>
</template>

<style scoped lang="scss">
.modal-lg {
  max-width: 800px;
}

.form-check-label {
  margin-left: 0.5rem;
  cursor: pointer;
}

.form-check-label strong {
  display: block;
  margin-bottom: 0.25rem;
}

.form-check-label .text-muted {
  font-size: 0.875rem;
}

.modal {
  z-index: 1055;
}

.modal-backdrop {
  z-index: 1050;
}

.form-check-input:checked ~ .form-check-label {
  color: var(--bs-primary);
}
</style>
